import { useRef, useEffect } from 'react';
import { useAnimationFrame } from 'framer-motion';

const ICON_PATHS = [
  '/xp/icons/icon__computer--large.png',
  '/xp/icons/icon__control_panel--large.png',
  '/xp/icons/icon__documents--large.png',
  '/xp/icons/icon__hdd--large.png',
  '/xp/icons/icon__network_places--large.png',
  '/xp/icons/icon__notepad--large.png',
  '/xp/icons/icon__pictures--large.png',
  '/xp/icons/icon__publish_web--large.png',
  '/xp/icons/icon__recycle_bin--large.png',
  '/xp/icons/icon__search--large.png',
];

const POOL_SIZE = 10;
const FADE_IN_MS = 2000;
const HOLD_MS = 8000;
const FADE_OUT_MS = 2000;
const CYCLE_MS = FADE_IN_MS + HOLD_MS + FADE_OUT_MS;
const STAGGER_MS = 2500;

function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return s / 2147483647;
  };
}

interface Bouncer {
  icon: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  birthOffset: number;
}

function createBouncers(): Bouncer[] {
  const rng = seededRandom(42);
  return Array.from({ length: POOL_SIZE }, (_, i) => {
    const icon = ICON_PATHS[i % ICON_PATHS.length]!;
    const speed = 40 + rng() * 50;
    const angle = rng() * Math.PI * 2;
    return {
      icon,
      x: rng() * 80 + 10,
      y: rng() * 80 + 10,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 50 + rng() * 50,
      birthOffset: i * STAGGER_MS,
    };
  });
}

export function FloatingIcons() {
  const bouncers = useRef(createBouncers()).current;
  const elRefs = useRef<(HTMLImageElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevTimeRef = useRef(0);
  const startRef = useRef(0);

  useEffect(() => {
    elRefs.current = elRefs.current.slice(0, POOL_SIZE);
  }, []);

  useAnimationFrame((time) => {
    if (startRef.current === 0) {
      startRef.current = time;
      prevTimeRef.current = time;
    }
    const dt = Math.min((time - prevTimeRef.current) / 1000, 0.1);
    prevTimeRef.current = time;
    const elapsed = time - startRef.current;

    const container = containerRef.current;
    if (!container) return;
    const cw = container.offsetWidth;
    const ch = container.offsetHeight;

    for (let i = 0; i < bouncers.length; i++) {
      const b = bouncers[i]!;
      const el = elRefs.current[i];
      if (!el) continue;

      const age = elapsed - b.birthOffset;
      if (age < 0) {
        el.style.opacity = '0';
        continue;
      }

      const cycleAge = age % CYCLE_MS;
      let opacity: number;
      if (cycleAge < FADE_IN_MS) {
        opacity = (cycleAge / FADE_IN_MS) * 0.32;
      } else if (cycleAge < FADE_IN_MS + HOLD_MS) {
        opacity = 0.32;
      } else {
        opacity =
          (1 - (cycleAge - FADE_IN_MS - HOLD_MS) / FADE_OUT_MS) * 0.32;
      }

      b.x += b.vx * dt;
      b.y += b.vy * dt;

      const maxX = cw - b.size;
      const maxY = ch - b.size;

      if (b.x <= 0) { b.x = 0; b.vx = Math.abs(b.vx); }
      else if (b.x >= maxX) { b.x = maxX; b.vx = -Math.abs(b.vx); }
      if (b.y <= 0) { b.y = 0; b.vy = Math.abs(b.vy); }
      else if (b.y >= maxY) { b.y = maxY; b.vy = -Math.abs(b.vy); }

      el.style.opacity = String(opacity);
      el.style.transform = `translate(${b.x}px, ${b.y}px)`;
    }
  });

  return (
    <div
      ref={containerRef}
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {bouncers.map((b, i) => (
        <img
          key={i}
          ref={(el) => { elRefs.current[i] = el; }}
          src={b.icon}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: b.size,
            height: b.size,
            objectFit: 'contain',
            filter: 'grayscale(0.4)',
            opacity: 0,
            willChange: 'transform, opacity',
          }}
        />
      ))}
    </div>
  );
}
